import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Copy } from 'lucide-react';
import { useWatch } from 'react-hook-form';
import type { CreateOrganizationResponse, OrganizationCreate } from '../../generated/api/types.gen.js';
import { zOrganizationCreate } from '../../generated/api/zod.gen.js';
import {
  createOrganizationMutation,
  listOrganizationsQueryKey,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { Button, Callout, Card, useToast } from '../../components/ui/index.js';
import { Form, SelectFormField, SubmitButton, TextFormField, useZodForm } from '../../components/form/index.js';
import { FormPage } from '../../components/page/FormPage.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import { Can } from '../../components/auth/Can.js';
import { PAGE_HELP } from '../../help/index.js';
import { invitationLink } from '../../lib/invitation-link.js';
import { ORGANIZATION_FIELD_HELP } from '../../help/organizations-fields.js';
import { toSlug } from '../../lib/slug.js';

const PLAN_OPTIONS = [
  { value: 'free', label: 'Free' },
  { value: 'standard', label: 'Standard' },
  { value: 'enterprise', label: 'Enterprise' },
];

interface CreatedProps {
  result: CreateOrganizationResponse;
  founderEmail: string;
}

/** Shown after the organization exists: the founder's invitation link, which the superadmin sends to the founder. */
const Created: React.FC<CreatedProps> = ({ result, founderEmail }) => {
  const { notify } = useToast();
  const { organization, invitationToken } = result;

  const link = invitationLink(invitationToken);

  // No email provider yet: the superadmin sends the founder this link (owner 2026-10-23).
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      notify('Invitation link copied');
    } catch {
      notify('Could not copy the link', 'danger');
    }
  };

  return (
    <div className="space-y-5 py-4 text-center">
      <CheckCircle size={44} className="mx-auto text-success" aria-hidden="true" />
      <div>
        <h2 className="text-xl font-semibold text-heading">{organization.name} created</h2>
        <p className="mt-1 text-sm text-muted">
          Send this invitation link to <span className="font-medium text-heading">{founderEmail}</span>: they create
          their account from it and join with the role you chose.
        </p>
      </div>

      <Callout tone="accent" title="Invitation link" className="text-left">
        <div className="mt-2 flex items-center gap-2">
          <code className="min-w-0 flex-1 overflow-x-auto rounded-sm bg-surface-alt px-2 py-1.5 font-mono text-xs text-heading">
            {link}
          </code>
          <Button variant="secondary" size="sm" iconOnly aria-label="Copy the invitation link" onClick={copyLink}>
            <Copy size={14} aria-hidden="true" />
          </Button>
        </div>
        <p className="mt-2 text-xs">It works only for {founderEmail}, and only once.</p>
      </Callout>

      <div className="flex justify-center gap-3">
        <Link
          to={`/admin/organizations/${organization.id}`}
          className={buttonStyles({ variant: 'primary', appearance: 'fill', size: 'sm' })}
        >
          Open organization
        </Link>
        <Link
          to="/admin/organizations"
          className={buttonStyles({ variant: 'primary', appearance: 'outline', size: 'sm' })}
        >
          Back to organizations
        </Link>
      </div>
    </div>
  );
};

export const NewOrganizationPage: React.FC = () => {
  const queryClient = useQueryClient();
  const create = useMutation(createOrganizationMutation());
  const [created, setCreated] = useState<{ result: CreateOrganizationResponse; founderEmail: string } | null>(null);

  const form = useZodForm(zOrganizationCreate, {
    defaultValues: { name: '', slug: '', plan: 'free', founderEmail: '' },
  });

  // The slug follows the name until the person types in the slug field themselves.
  const name = useWatch({ control: form.control, name: 'name' });
  const slugTouched = form.formState.dirtyFields.slug;
  useEffect(() => {
    if (!slugTouched) form.setValue('slug', toSlug(name ?? ''));
  }, [name, slugTouched, form]);

  const createOrganization = async (body: OrganizationCreate) => {
    const result = await create.mutateAsync({ body });
    await queryClient.invalidateQueries({ queryKey: listOrganizationsQueryKey() });
    setCreated({ result, founderEmail: body.founderEmail });
  };

  return (
    <FormPage
      title="New organization"
      subtitle="Creates the tenant and invites its first administrator."
      helpContent={PAGE_HELP.organizations}
      backTo="/admin/organizations"
      backLabel="Organizations"
      aside={
        <Card title="What happens next">
          <ol className="list-decimal space-y-2 pl-5 text-sm text-body">
            <li>The organization is active as soon as you create it.</li>
            <li>The founder receives an invitation by email.</li>
            <li>When they accept, they become the organization administrator and invite everyone else.</li>
          </ol>
        </Card>
      }
    >
      {created ? (
        <Created result={created.result} founderEmail={created.founderEmail} />
      ) : (
        <Form form={form} onSubmit={createOrganization} guardUnsaved>
          <TextFormField
            name="name"
            label="Name"
            placeholder="Acme Corp"
            help={ORGANIZATION_FIELD_HELP.name}
            required
          />
          <TextFormField
            name="slug"
            label="Slug"
            placeholder="acme-corp"
            description="Taken from the name. It cannot be changed after creation."
            help={ORGANIZATION_FIELD_HELP.slug}
            required
          />
          <SelectFormField name="plan" label="Plan" options={PLAN_OPTIONS} help={ORGANIZATION_FIELD_HELP.plan} />
          <TextFormField
            name="founderEmail"
            label="Founder email"
            type="email"
            placeholder="founder@example.com"
            description="The founder joins as organization administrator."
            help={ORGANIZATION_FIELD_HELP.founderEmail}
            required
          />
          <Can policy="CanCreateOrgs">
            <div>
              <SubmitButton>Create organization</SubmitButton>
            </div>
          </Can>
        </Form>
      )}
    </FormPage>
  );
};
